import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Receipt } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { PartnerService } from '@/services/modules/PartnerService'
import type { Revenue, RevenueStatement } from '@/services/modules/RevenuesService'
import { brl, dateBR } from '@/lib/format'

export function PartnerInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [revenue, setRevenue] = useState<Revenue | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    PartnerService.invoice(id).then(setRevenue).catch(() => setRevenue(null)).finally(() => setLoading(false))
  }, [id])
  useEffect(() => { load() }, [load])

  if (loading) return <div className="text-sm text-gray-500">Carregando…</div>
  if (!revenue) return <div className="text-sm text-gray-500">Fatura não encontrada.</div>

  const rev = revenue
  const isBoleto = (rev.method ?? '').toUpperCase() === 'BOLETO'
  const statements = rev.statements ?? []
  const customer = rev.subscriber?.name ?? rev.company?.name ?? '—'

  const subtotal = statements.reduce((a, s) => a + (s.planAmount || 0), 0)
  const discounts = statements.reduce((a, s) => a + (s.discountAmount || 0), 0)
  const total = statements.reduce((a, s) => a + (s.amount || 0), 0)

  const columns: DataTableColumn<RevenueStatement>[] = [
    { key: 'sequence', header: 'Parcela', align: 'center', render: s => s.sequence === 9999 ? '—' : `#${s.sequence}` },
    { key: 'subscriber', header: 'Assinante', render: s => s.subscriber?.name ?? '—' },
    { key: 'coupon', header: 'Cupom', align: 'center', render: s => s.couponCode ?? '—' },
    { key: 'plan', header: 'Plano', align: 'right', render: s => s.formatted?.planAmount ?? brl(s.planAmount) },
    { key: 'discount', header: 'Desconto', align: 'right', render: s => (s.discountAmount > 0 ? `- ${s.formatted?.discountAmount ?? brl(s.discountAmount)}` : '—') },
    { key: 'amount', header: 'Total', align: 'right', render: s => <span className="font-medium text-gray-800">{s.formatted?.amount ?? brl(s.amount)}</span> },
  ]

  return (
    <>
      <PageHeader
        title={`Fatura ${rev.code}`}
        subtitle="Detalhes da fatura"
        crumbs={[{ label: 'Financeiro' }, { label: 'Faturas', to: '/partner/invoices' }, { label: rev.code }]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/partner/invoices')} iconLeft={<ArrowLeft size={14} />}>Voltar</Button>
            {isBoleto && rev.bankSlipUrl && (
              <Button onClick={() => window.open(rev.bankSlipUrl!, '_blank', 'noopener,noreferrer')} iconLeft={<Receipt size={14} />}>Ver boleto</Button>
            )}
          </div>
        }
      />

      <Card className="mb-4">
        <CardHeader title="Resumo" />
        <CardBody>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3">
            <Field label="Cliente" value={customer} />
            <Field label="Tipo" value={rev.subscriberId ? 'Associado' : rev.companyId ? 'Empresa' : '—'} />
            <Field label="Status" value={<StatusBadge status={rev.status} />} />
            <Field label="Valor" value={rev.formatted?.amount ?? brl(rev.amount)} />
            <Field label="Vencimento" value={rev.formatted?.dueDate ?? dateBR(rev.dueDate)} />
            <Field label="Pago em" value={rev.paidDate ? (rev.formatted?.paidDate ?? dateBR(rev.paidDate)) : '—'} />
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Extrato" subtitle="Itens que compõem o valor desta fatura" />
        <CardBody>
          <DataTable columns={columns} rows={statements} getRowKey={s => s.id} emptyMessage="Nenhum item de extrato." />
          <div className="mt-8 flex justify-end border-t border-gray-100 pt-5">
            <dl className="w-full max-w-xs space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-600"><dt>Subtotal</dt><dd>{brl(subtotal)}</dd></div>
              <div className="flex justify-between text-gray-600"><dt>Descontos</dt><dd className={discounts > 0 ? 'text-rose-600' : ''}>{discounts > 0 ? `- ${brl(discounts)}` : brl(0)}</dd></div>
              <div className="flex justify-between border-t border-gray-200 pt-1.5 font-semibold text-gray-800"><dt>Total</dt><dd>{brl(total)}</dd></div>
            </dl>
          </div>
        </CardBody>
      </Card>
    </>
  )
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-gray-500">{label}</dt>
      <dd className="mt-0.5 font-medium text-gray-800">{value}</dd>
    </div>
  )
}

export default PartnerInvoiceDetailPage
